import { useCallback, useEffect, useState } from 'react'
import * as React from 'react'
import Event from '../../Event'

import { Fieldset, Legend, SearchableDropdown, SelectOption } from '@paytrace/pt-skel/Component'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import { Recur } from '../../types'

interface RecurSelectProps {
  merchantId: number;
  handleRecurChange: (recur: Recur) => void;
  recurIdOverWrite?: number;
}

interface SelectOpt { name: string; val: number | string; }

const defaultRecurList: SelectOpt[] = [
  { name: '-- Customer Name / Amount / ID --', val: '' },
]

const self = Symbol('RecurSelect')

function debounce<T extends (...args: any[]) => void>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(() => {
      func(...args)
    }, wait)
  }
}

function RecursToOpts (recurs: Recur[]): SelectOpt[] {
  if (!recurs) return []
  return recurs.map(recur => ({
    name: `${recur.customer_billing_name || 'N/A'} / $${recur.amount?.toFixed(2) ?? '0.00'} / ${recur.recurring_id ?? 'N/A'}`,
    val: recur.recurring_id,
  }))
}

export default function RecurSelect ({
  merchantId,
  handleRecurChange,
  recurIdOverWrite,
}: RecurSelectProps): React.ReactElement {
  const [allRecurData, setAllRecurData] = useState<Recur[]>([])
  const [dropdownOptions, setDropdownOptions] = useState<SelectOpt[]>(defaultRecurList)
  const [selectedOption, setSelectedOption] = useState<SelectOption | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')

  useEffect(() => {
    const handleListResponse = (recurs: Recur[] | null) => {
      setIsLoading(false)
      if (recurs) {
        setAllRecurData(recurs)
        const recurOpts = RecursToOpts(recurs)
        setDropdownOptions([...defaultRecurList, ...recurOpts])
      } else {
        setAllRecurData([])
        setDropdownOptions(defaultRecurList)
      }
    }

    PubSub.sub(self, Event.GET_RECURRENCE_LIST_RESPONSE, handleListResponse)
    PubSub.pub(Event.GET_RECURRENCE_LIST_REQUEST, { merchant_id: merchantId })

    return () => {
      PubSub.unsub(Event.GET_RECURRENCE_LIST_RESPONSE)
    }
  }, [merchantId, searchTerm])

  const handleSelectionChange = useCallback((selection: SelectOption) => {
    setSelectedOption(selection)
    if (selection && selection.val) {
      const selectedRecur = allRecurData.find(recur => recur.recurring_id === Number(selection.val))
      if (selectedRecur) {
        handleRecurChange(selectedRecur)
      }
    } else {
      // handleRecurChange(null);
    }
  }, [allRecurData, handleRecurChange])

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      if (!merchantId) return

      setIsLoading(true)
      const updatedSearchObject = { merchant_id: merchantId, search_term: term }
      PubSub.pub(Event.GET_RECURRENCE_LIST_REQUEST, updatedSearchObject)
    }, 500),
    [merchantId]
  )

  const handleSearchInputChange = (term: string) => {
    setSearchTerm(term)
    if (term.trim() === '') {
      setIsLoading(false)
      setAllRecurData([])
      setDropdownOptions(defaultRecurList)
    } else {
      debouncedSearch(term)
    }
  }

  useEffect(() => {
    if (recurIdOverWrite && allRecurData && dropdownOptions.length > 1) {
      const findRecuring = dropdownOptions.find(
        (recur) => Number(recur.val) === Number(recurIdOverWrite)
      )

      if (findRecuring) {
        setSelectedOption(findRecuring as SelectOption)
      }

      const selectedRecur = allRecurData.find(
        (recur) => recur.recurring_id === Number(recurIdOverWrite)
      )
      if (selectedRecur) {
        handleRecurChange(selectedRecur)
      }
    }
  }, [isLoading, allRecurData, dropdownOptions, handleRecurChange, recurIdOverWrite])

  return (
    <Fieldset>
      <Legend title="Find Recurring Payment" />
      <SearchableDropdown
        options={dropdownOptions}
        handleSearchChange={handleSearchInputChange}
        handleSelectionChange={handleSelectionChange}
        selectedValue={selectedOption}
        label="Recurring Payment"
        placeHolder={defaultRecurList[0].name}
      />
    </Fieldset>
  )
}
